import { isoDateIn, zonedInstant } from '@glory/kernel';

import { type IsoDate } from './deal.ts';
import { addDays, clockMinutes, clockText } from './step.ts';

export const DEFAULT_QUEUE_REMINDER_MINUTES = 30;
export const DEFAULT_WORK_START = '10:00';
export const DEFAULT_WORK_END = '19:00';
export const QUEUE_REMINDER_MIN = 1;
export const QUEUE_REMINDER_MAX = 480;

/** Утренняя сводка продаж приходит в 9:00 рабочего дня. */
export const DIGEST_MINUTES = 9 * 60;
/**
 * Если в 9:00 worker не работал, сводка догоняет, но только до этого часа: утренняя сводка после обеда
 * уже не утренняя.
 */
export const DIGEST_CUTOFF_MINUTES = 13 * 60;

/** Рабочее время студии: пояс, часы из настроек и рабочие дни по производственному календарю. */
export interface WorkSchedule {
  readonly timeZone: string;
  /** Минуты от полуночи. */
  readonly workStart: number;
  readonly workEnd: number;
  /** Рабочий ли день — решает календарь (модуль `org`), в domain он приходит готовым ответом. */
  readonly isWorkingDay: (date: IsoDate) => boolean;
}

/** Окно дня в рабочих минутах не пустое и помещается в сутки. */
export const isWorkWindow = (start: number, end: number): boolean =>
  Number.isInteger(start) && Number.isInteger(end) && start >= 0 && end <= 24 * 60 && start < end;

/** Часы из настроек (`ЧЧ:ММ` или `ЧЧ:ММ:СС`) в минуты от полуночи. */
export function workWindow(start: string, end: string): { start: number; end: number } | null {
  const from = clockMinutes(start);
  const to = clockMinutes(end);
  return from === null || to === null || !isWorkWindow(from, to) ? null : { start: from, end: to };
}

export const isQueueReminderMinutes = (value: number): boolean =>
  Number.isInteger(value) && value >= QUEUE_REMINDER_MIN && value <= QUEUE_REMINDER_MAX;

/** Верхняя граница перебора дней: таймер очереди не нужен для лидов старше трёх лет. */
const MAX_DAYS = 1_100;

/**
 * Рабочие минуты между двумя моментами: только рабочие дни календаря и только часы из настроек (в
 * выходные, праздники и вне часов таймер стоит).
 */
export function workingMinutesBetween(from: Date, to: Date, schedule: WorkSchedule): number {
  if (to.getTime() <= from.getTime()) return 0;
  const last = isoDateIn(schedule.timeZone, to);
  let day = isoDateIn(schedule.timeZone, from);
  let ms = 0;
  for (let i = 0; i < MAX_DAYS && day <= last; i += 1) {
    if (schedule.isWorkingDay(day)) {
      const start = zonedInstant(schedule.timeZone, day, schedule.workStart).getTime();
      const end = zonedInstant(schedule.timeZone, day, schedule.workEnd).getTime();
      const low = Math.max(start, from.getTime());
      const high = Math.min(end, to.getTime());
      if (high > low) ms += high - low;
    }
    day = addDays(day, 1);
  }
  return Math.floor(ms / 60_000);
}

/** Сколько рабочих минут лид ждёт в очереди. */
export const queueWaitMinutes = (queuedAt: Date, now: Date, schedule: WorkSchedule): number =>
  workingMinutesBetween(queuedAt, now, schedule);

/**
 * Пора напомнить о лиде в очереди: прошло не меньше `every` рабочих минут с постановки (первое
 * напоминание) или с прошлого напоминания (следующие). Возвращает, сколько минут лид уже ждёт
 * (для текста «Лид ждёт уже N минут»), либо null, если рано.
 */
export function queueReminderDue(
  input: { readonly queuedAt: Date; readonly remindedAt: Date | null; readonly every: number },
  now: Date,
  schedule: WorkSchedule,
): number | null {
  const waited = queueWaitMinutes(input.queuedAt, now, schedule);
  const since = input.remindedAt === null ? waited : workingMinutesBetween(input.remindedAt, now, schedule);
  return since >= input.every ? waited : null;
}

/**
 * Пора слать утреннюю сводку: сегодня рабочий день, на часах от 9:00 и до границы догонки, а сегодня её
 * ещё не слали.
 */
export function digestDue(
  input: { readonly today: IsoDate; readonly minutesOfDay: number; readonly sentOn: IsoDate | null },
  isWorkingToday: boolean,
): boolean {
  return (
    isWorkingToday &&
    input.sentOn !== input.today &&
    input.minutesOfDay >= DIGEST_MINUTES &&
    input.minutesOfDay < DIGEST_CUTOFF_MINUTES
  );
}

/** Часы работы для подписи: «10:00–19:00». */
export const workHoursText = (start: number, end: number): string => `${clockText(start)}–${clockText(end)}`;
